import { onBeforeUnmount, onMounted, ref } from "vue";

export const DEFAULT_LAUNCHER_ICON_SOURCES = [
  "/wp-content/uploads/2025/09/mascot-chatbot.png",
  "/wp-content/uploads/2025/09/Favicon-Prestech-Fundo-Branco.svg"
] as const;

export type LauncherIconState =
  | { kind: "loading" }
  | { kind: "image"; src: string }
  | { kind: "fallback" };

export interface LauncherIconResolverOptions {
  sources?: readonly string[];
  imageFactory?: () => HTMLImageElement;
}

const browserImageFactory = (): HTMLImageElement => new Image();

/**
 * Validates launcher image sources in priority order without rendering a broken
 * image into the button.
 */
export const resolveLauncherIcon = async (
  options: LauncherIconResolverOptions = {}
): Promise<LauncherIconState> => {
  const sources = options.sources ?? DEFAULT_LAUNCHER_ICON_SOURCES;
  const imageFactory = options.imageFactory ?? browserImageFactory;

  for (const source of sources) {
    if (await canLoadImage(source, imageFactory)) {
      return { kind: "image", src: source };
    }
  }

  return { kind: "fallback" };
};

/**
 * Vue composable that exposes the async launcher icon state used by the widget.
 */
export const useLauncherIcon = (options: LauncherIconResolverOptions = {}) => {
  const state = ref<LauncherIconState>({ kind: "loading" });
  let mounted = true;

  const refresh = async (): Promise<void> => {
    state.value = { kind: "loading" };
    const nextState = await resolveLauncherIcon(options);
    if (mounted) {
      state.value = nextState;
    }
  };

  onMounted(() => {
    void refresh();
  });

  onBeforeUnmount(() => {
    mounted = false;
  });

  return { state, refresh };
};

const canLoadImage = (
  source: string,
  imageFactory: () => HTMLImageElement
): Promise<boolean> =>
  new Promise((resolve) => {
    let image: HTMLImageElement;
    try {
      image = imageFactory();
    } catch {
      resolve(false);
      return;
    }

    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = source;
  });
